import { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import noop from 'lib/function/noop'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import ChatItemDropdown from './ChatItemDropdown'
import ModalContext from 'contexts/ModalContext'
import { useDeleteStopMutation } from 'tripPlanner/hooks/api/chat'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import ConfirmDeleteStopModal from './ConfirmDeleteStopModal'
import { CANVAS_CONTENT_WIDTH } from './constants'
import Heading from 'LuxKit/Typography/Heading'

const OriginContainer = styled.div`
  width: ${rem(CANVAS_CONTENT_WIDTH)};
`

const IconCircle = styled.div`
  width: ${rem(32)};
  height: ${rem(32)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  background: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: ${props => props.theme.shadow.flat.small};
  font-size: ${rem(20)};
  position: relative;
  z-index: 2;
`

interface Props {
  stopId: number
  placeName: string
  placeCountry: string
  countryFlag?: string
  onRefetchItinerary?: () => void
}

function TripPlanOriginStop({
  stopId,
  placeName,
  placeCountry,
  countryFlag,
  onRefetchItinerary = noop,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const { mutate: deleteStop, isPending: isDeleting } = useDeleteStopMutation()

  const handleDelete = useCallback(async() => {
    const confirmed = await showModal<boolean>(<ConfirmDeleteStopModal placeName={placeName} />)
    if (!confirmed) return

    deleteStop(
      { stopId },
      {
        onSuccess: () => {
          showSnackbar(intl.formatMessage({
            id: 'sZS1KgaynTEpPs62TQkB',
            defaultMessage: 'Stop deleted successfully',
            description: 'Success snackbar message after deleting an origin stop',
          }), 'success')
          onRefetchItinerary()
        },
        onError: () => {
          showSnackbar(intl.formatMessage({
            id: '9Zz0XBxhBWU9yGR2gVV1',
            defaultMessage: 'Failed to delete stop. Please try again.',
            description: 'Error snackbar message when deleting an origin stop fails',
          }), 'critical')
        },
      },
    )
  }, [showModal, placeName, deleteStop, stopId, onRefetchItinerary, intl])

  return (
    <OriginContainer>
      <Group direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="start">
        <Group direction="horizontal" gap={20} verticalAlign="center" horizontalAlign="center">
          {countryFlag && <IconCircle>{countryFlag}</IconCircle>}
          <Group direction="vertical" gap={2}>
            <Caption variant="medium" colour="neutral-three">
              <FormattedMessage
                id="grOmPd5l2L1uDX16RpKy"
                defaultMessage="Your location"
                description="Label above the origin city name in the trip itinerary"
              />
            </Caption>
            <Heading variant="heading4" as="span">
              {placeName}, {placeCountry}
            </Heading>
          </Group>
        </Group>
        <ChatItemDropdown onDelete={isDeleting ? undefined : handleDelete} />
      </Group>
    </OriginContainer>
  )
}

export default TripPlanOriginStop
